import React, { useState, useEffect, useRef, useMemo } from 'react';
import cn from 'classnames';
import Container from 'components/UI/Container';
import Button from 'components/UI/Button';
import Markup from 'components/Markup';
import { observer, cookies as cookiesStore, modal as modalStore } from 'store';
import {
  CONSENT_COOKIE_NAME,
  CONSENT_MODAL_ID,
  getCookieOptions,
  BANNER_STORAGE_KEY,
  DEFAULT_CONSENT,
} from 'define/cookies';
import { setCookie, getCookie } from 'utils/cookie';
import { handleGLobalLinksInText } from 'utils';
import { useTrans, useLang, useLocalisedPrivacyLinks } from 'utils/localisation';
import { updateConsentStatus } from 'utils/consentManager';
import { trackVisitor } from 'utils/visitorTracking';
import * as styles from './CookiesPolicy.module.scss';

function CookiesPolicy() {
  const descriptionRef = useRef();
  const lang = useLang();
  const { privacyPolicyLink, cookiesPolicyLink } = useLocalisedPrivacyLinks();
  const bannerTheme = cookiesStore.theme;
  const t = useTrans(['consentBanner']);
  const content = t(bannerTheme);
  const descriptionWithBreak = content?.description?.includes('\r');
  const [fullDescription, setFullDescription] = useState(!descriptionWithBreak);
  const [consent, setConsent] = useState(DEFAULT_CONSENT);
  const [layout, setLayout] = useState(null);

  const acceptCookies = () => {
    setCookie(CONSENT_COOKIE_NAME, JSON.stringify(consent), getCookieOptions());
    updateConsentStatus(consent);
    trackVisitor();
  };

  const onAccept = () => {
    if (layout === 'gdpr') {
      acceptCookies();
    }
    localStorage.setItem(BANNER_STORAGE_KEY, 'seen');
    cookiesStore.setSeen(true);
  };

  const onManage = () => {
    modalStore.open(CONSENT_MODAL_ID);
    cookiesStore.setSeen(true);
  };

  useEffect(() => {
    const consentStatus = JSON.parse(getCookie(CONSENT_COOKIE_NAME));
    if (consentStatus) {
      setConsent(prev => ({ ...prev, ...consentStatus }));
    }
  }, []);

  useEffect(() => {
    if (bannerTheme) {
      setLayout(bannerTheme);
    }
  }, [bannerTheme]);

  const finalDescription = useMemo(() => {
    const description = content?.description
      ?.replace(/\{privacyPolicyLink\}/g, privacyPolicyLink)
      ?.replace(/\{cookiesPolicyLink\}/g, cookiesPolicyLink);

    if (layout === 'gdpr' && !fullDescription) {
      return description?.slice(0, description.indexOf('\r'));
    }

    return description;
  }, [content, fullDescription, layout, privacyPolicyLink, cookiesPolicyLink]);

  useEffect(() => {
    if (lang === 'ae' || lang === 'az') {
      handleGLobalLinksInText(descriptionRef);
    }
  }, [finalDescription, lang]);

  if (!layout) {
    return null;
  }

  return (
    <div className={cn(styles.wrapper, styles[layout], cookiesStore.seen && styles.hidden)}>
      <Container padding="Without" background="transparent" className={styles.container}>
        <div className={styles.contentWrapper}>
          <div className={styles.content}>
            <div>
              <Markup ref={descriptionRef} tag="span" className={styles.text}>
                {finalDescription}
              </Markup>
              {layout === 'gdpr' && content?.showMore && descriptionWithBreak && (
                <button
                  onClick={() => setFullDescription(!fullDescription)}
                  className={cn(styles.showMore, fullDescription && styles.active)}
                >
                  {!fullDescription ? content?.showMore : content?.hide}
                </button>
              )}
            </div>
          </div>
          {content?.manage && layout === 'gdpr' && (
            <button className={styles.manageButton} onClick={onManage}>
              {content?.manage}
            </button>
          )}
          <Button className={styles.button} onClick={onAccept} fullWidthMobile>
            {content?.accept}
          </Button>
        </div>
      </Container>
    </div>
  );
}

export default observer(CookiesPolicy);
